/**
 * Transaction Utilities
 * 
 * Utility functions for formatting and extracting data from transactions.
 */

import { format } from "date-fns";
import { id as idLocale } from "date-fns/locale";
import type { HistoricalTransaction } from "@/services/paymentService";
import { cn } from "@/lib/utils";

/**
 * Format date to Indonesian format (DD/MM/YYYY)
 */
export function formatDate(dateString: string): string {
  try {
    const date = new Date(dateString);
    return format(date, "dd/MM/yyyy", { locale: idLocale });
  } catch {
    return "-";
  }
}

/**
 * Extract callsign from paymentData
 * order_id format: "YB8MR-ORP-gofoundry-10226946-1762278616489"
 * We extract just "YB8MR" (first part before "-")
 */
export function getCallsign(transaction: HistoricalTransaction): string {
  // Try to get from payementData.transactionDetails.order_id
  if (transaction.payementData?.transaction_details?.order_id) {
    const orderId = transaction.payementData.transaction_details.order_id;
    // Split by "-" and get first part (e.g., "YB8MR" from "YB8MR-ORP-gofoundry-...")
    const parts = orderId.split("-");
    if (parts.length > 0 && parts[0]) {
      return parts[0].trim();
    }
  }
  // Fallback to populated user data
  return transaction.user?.callsign || "-";
}

/**
 * Extract name from paymentData
 */
export function getName(transaction: HistoricalTransaction): string {
  // Try to get from paymentData.customer_details.first_name
  if (transaction.payementData?.customer_details?.first_name) {
    return transaction.payementData.customer_details.first_name;
  }
  // Fallback to populated user data
  return transaction.user?.nama_lengkap || "-";
}

/**
 * Format nominal/amount
 */
export function formatNominal(transaction: HistoricalTransaction): string {
  // Try to extract amount from paymentData.transactionDetails.grossAmount
  if (transaction.payementData?.transaction_details?.gross_amount) {
    const amount = transaction.payementData.transaction_details.gross_amount;
    return new Intl.NumberFormat("id-ID", {
      style: "currency",
      currency: "IDR",
      minimumFractionDigits: 0,
    }).format(amount);
  }
  // Fallback to other paymentData fields
  if (transaction.payementData?.amount) {
    return new Intl.NumberFormat("id-ID", {
      style: "currency",
      currency: "IDR",
      minimumFractionDigits: 0,
    }).format(transaction.payementData.amount);
  }
  if (transaction.paymentGatewayData?.gross_amount) {
    return new Intl.NumberFormat("id-ID", {
      style: "currency",
      currency: "IDR",
      minimumFractionDigits: 0,
    }).format(transaction.paymentGatewayData.gross_amount);
  }
  if (transaction.paymentGatewayData?.amount) {
    return new Intl.NumberFormat("id-ID", {
      style: "currency",
      currency: "IDR",
      minimumFractionDigits: 0,
    }).format(transaction.paymentGatewayData.amount);
  }
  return "-";
}

/**
 * Get status badge color classes
 */
export function getStatusColor(status: string): string {
  switch (status.toLowerCase()) {
    case "settlement":
    case "success":
      return "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200";
    case "pending":
      return "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200";
    case "expire":
    case "cancel":
    case "deny":
      return "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200";
    default:
      return "bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200";
  }
}

/**
 * Get payment type with friendly display name
 */
export function getPaymentType(transaction: HistoricalTransaction): string {
  // Try paymentGatewayData with capitalized key "Payment Type" (Midtrans format)
  if (transaction.paymentGatewayData?.["Payment Type"]) {
    return (transaction.paymentGatewayData["Payment Type"] as string).toLowerCase();
  }
  // Try paymentGatewayData with snake_case
  if (transaction.paymentGatewayData?.payment_type) {
    return transaction.paymentGatewayData.payment_type;
  }
  // Try midtransStatus
  if (transaction.midtransStatus?.payment_type) {
    return transaction.midtransStatus.payment_type;
  }
  return "-";
}

/**
 * Get payment type logo/icon identifier
 */
export function getPaymentTypeLogo(transaction: HistoricalTransaction): string {
  const paymentType = getPaymentType(transaction);

  const typeMapping: Record<string, string> = {
    "bank_transfer": "bank",
    "credit_card": "credit-card",
    "debit_card": "credit-card",
    "e_wallet": "wallet",
    "gopay": "gopay",
    "ovo": "ovo",
    "dana": "dana",
    "bca_va": "bca",
    "mandiri_va": "mandiri",
    "cimb_va": "cimb",
    "bni_va": "bni",
    "bri_va": "bri",
  };

  return typeMapping[paymentType] || "default";
}

/**
 * Format payment type for display
 */
export function formatPaymentType(paymentType: string): string {
  const displayNames: Record<string, string> = {
    "bank_transfer": "Bank Transfer",
    "credit_card": "Kartu Kredit",
    "debit_card": "Kartu Debit",
    "e_wallet": "E-Wallet",
    "gopay": "GoPay",
    "ovo": "OVO",
    "dana": "DANA",
    "bca_va": "BCA Virtual Account",
    "mandiri_va": "Mandiri Virtual Account",
    "cimb_va": "CIMB Virtual Account",
    "bni_va": "BNI Virtual Account",
    "bri_va": "BRI Virtual Account",
  };

  return displayNames[paymentType] || paymentType;
}

/**
 * Extract settlement time from payment gateway data
 */
export function getSettlementTime(transaction: HistoricalTransaction): string | null {
  const raw =
    transaction.paymentGatewayData?.settlement_time ??
    transaction.paymentGatewayData?.settlementTime ??
    transaction.midtransStatus?.settlement_time ??
    null;
  if (!raw) return null;

  try {
    const date = new Date(raw);
    if (Number.isNaN(date.getTime())) return null;
    return format(date, "dd/MM/yyyy HH:mm", { locale: idLocale });
  } catch {
    return null;
  }
}

